import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import type { JSX } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import {
  Button,
  Checkbox,
  Flex,
  Heading,
  InfoIcon,
  Link,
  Notice,
  Popover,
  Separator,
  Switch,
  Text,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'

import EmailNotificationsSkeleton from './components/EmailNotificationsSkeleton'
import { useCustomerIOPreferences } from './hooks/useCustomerIOPreferences'
import type {
  DailyCommunityRecapTopicId,
  DailyRecapTopicId,
  HourlyAlertTopicId,
  TopicId,
} from './hooks/useCustomerIOPreferences'
import {
  useEmailNotificationPreferences,
  ACTIVITY_ALERTS_GROUP,
  INSIGHTS_PERFORMANCE_GROUP,
  TIPS_EDUCATION_GROUP,
  NEWSLETTERS_GROUP,
  RESEARCH_FEEDBACK_GROUP,
} from './hooks/useEmailNotificationPreferences'
import { usePushNotificationPreferences } from './hooks/usePushNotificationPreferences'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsRow } from '../shared/components/SettingsRow'

import styles from './EmailNotifications.module.css'

type NotificationUpdatePayload = {
  id: string
  subscribed: boolean
}

type BaseNotificationPreference = {
  id: string
  name: string
  description: string
  subscribed: boolean
}

/**
 * The email topics that have a push notification counterpart in Customer.io,
 * keyed by email preference id. The values are the push preference ids served
 * by the `pushNotificationPreferences` query.
 */
const EMAIL_TO_PUSH_PREFERENCE_ID: Record<string, string> = {
  queue: 'bufferEmpty',
  postFailure: 'sendFailedUpdate',
  collaboration: 'sendNewContribution',
  reminders: 'sendInactive7Days',
}

const COLLABORATION_PREFERENCE_ID = 'collaboration'
const COLLABORATION_NOTES_DESCRIPTION =
  'Sends updates about team activity like contributions, note mentions, and approval requests.'

function collaborationPreferenceForDisplay(
  preference: BaseNotificationPreference,
  {
    isReady,
    isEnabled,
  }: {
    isReady: boolean
    isEnabled: boolean
  },
): BaseNotificationPreference {
  if (preference.id !== COLLABORATION_PREFERENCE_ID || !isReady || !isEnabled) {
    return preference
  }

  return { ...preference, description: COLLABORATION_NOTES_DESCRIPTION }
}

/**
 * Drives one preference control: it moves as soon as the user acts, and
 * moves back if the write fails, so the control never sits on a value the
 * server rejected.
 */
function useOptimisticPreference({
  preferenceId,
  subscribed,
  updateNotificationSetting,
}: {
  preferenceId: string
  subscribed: boolean
  updateNotificationSetting: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
}): { isOn: boolean; handleChange: (value: boolean) => Promise<void> } {
  const [isOn, setIsOn] = useState(subscribed)
  // The last value the server accepted. A failed write rolls back to this
  // rather than to whatever was on screen when it started: two overlapping
  // writes each capture a different on-screen value, so rolling back to that
  // can leave the control showing a value that was never stored.
  const confirmedValue = useRef(subscribed)

  // Sync local state with subscribed prop when it changes
  useEffect(() => {
    setIsOn(subscribed)
    confirmedValue.current = subscribed
  }, [subscribed])

  const handleChange = useCallback(
    async (value: boolean): Promise<void> => {
      setIsOn(value)

      const success = await updateNotificationSetting({
        id: preferenceId,
        subscribed: value,
      })
      if (success) {
        confirmedValue.current = value
      } else {
        setIsOn(confirmedValue.current)
      }
    },
    [preferenceId, updateNotificationSetting],
  )

  return { isOn, handleChange }
}

const PreferenceSwitch = ({
  switchId,
  preferenceId,
  subscribed,
  updateNotificationSetting,
  disabled,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: {
  switchId: string
  preferenceId: string
  subscribed: boolean
  updateNotificationSetting: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
  disabled: boolean
  'aria-labelledby': string
  'aria-describedby': string
}): JSX.Element => {
  const { isOn, handleChange } = useOptimisticPreference({
    preferenceId,
    subscribed,
    updateNotificationSetting,
  })

  return (
    <Switch
      id={switchId}
      onCheckedChange={(value) => {
        void handleChange(value)
      }}
      checked={isOn}
      disabled={disabled}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    />
  )
}

const DAILY_POST_RECAP_PREFERENCE_ID = 'dailyPostRecap'
// Keep the owner sentence identical to dailyPostRecap in
// libraries/customerio-http/src/cio-subscriptions.ts.
const DAILY_POST_RECAP_TOPICS_DESCRIPTION =
  'Sends a daily summary of post performance and activity. Sent for any organization you own, covering all of its channels.'

function dailyPostRecapPreferenceForDisplay(
  preference: BaseNotificationPreference,
  showDailyRecapTopics: boolean,
): BaseNotificationPreference {
  if (
    preference.id !== DAILY_POST_RECAP_PREFERENCE_ID ||
    !showDailyRecapTopics
  ) {
    return preference
  }

  return { ...preference, description: DAILY_POST_RECAP_TOPICS_DESCRIPTION }
}

const DAILY_COMMUNITY_RECAP_PREFERENCE_ID = 'dailyCommunityRecap'

const HOURLY_EMAILS_PREFERENCE_ID = 'hourlyEmails'
const HOURLY_EMAILS_TOPICS_NAME = 'Hourly Community Alerts'
const HOURLY_EMAILS_TOPICS_DESCRIPTION =
  'Sends an hourly summary of new items across Community-supported channels.'

function hourlyEmailsPreferenceForDisplay(
  preference: BaseNotificationPreference,
  showHourlyAlertTopics: boolean,
): BaseNotificationPreference {
  if (preference.id !== HOURLY_EMAILS_PREFERENCE_ID || !showHourlyAlertTopics) {
    return preference
  }

  return {
    ...preference,
    name: HOURLY_EMAILS_TOPICS_NAME,
    description: HOURLY_EMAILS_TOPICS_DESCRIPTION,
  }
}

/**
 * One topic a notification covers. Each id addresses the Customer.io
 * attribute of the same name.
 */
type TopicOption<Id extends TopicId> = {
  id: Id
  label: string
  /** A topic the notification always covers: shown checked and not editable. */
  locked?: boolean
}

/** What the Daily Post Recap covers, in the order the designs show them. */
const DAILY_RECAP_TOPICS: TopicOption<DailyRecapTopicId>[] = [
  { id: 'postsDaily', label: 'Posts & stats', locked: true },
  { id: 'commentsDaily', label: 'Comments' },
  { id: 'mentionsDaily', label: 'Mentions' },
]

/**
 * The Daily Community Recap topics a user can choose. The recap also covers
 * posts, but offers no box for them while the Daily Post Recap is a separate
 * notification: switching the recap on stores `communityPostsDaily` instead.
 */
const DAILY_COMMUNITY_RECAP_TOPICS: TopicOption<DailyCommunityRecapTopicId>[] =
  [
    { id: 'communityCommentsDaily', label: 'Comments' },
    { id: 'communityMentionsDaily', label: 'Mentions' },
  ]

const DAILY_POST_RECAP_SWITCH_ON_TOPICS: readonly TopicId[] = ['postsDaily']
const DAILY_COMMUNITY_RECAP_SWITCH_ON_TOPICS: readonly TopicId[] = [
  'communityPostsDaily',
  'communityCommentsDaily',
]
const HOURLY_ALERT_SWITCH_ON_TOPICS: readonly TopicId[] = ['commentsHourly']

/** What the Hourly Comment Alerts cover, in the order the designs show them. */
const HOURLY_ALERT_TOPICS: TopicOption<HourlyAlertTopicId>[] = [
  { id: 'commentsHourly', label: 'Comments' },
  { id: 'mentionsHourly', label: 'Mentions' },
]

/**
 * The on-screen value of each topic in a row. A box moves as soon as the user
 * acts and falls back to the stored value once its write settles, so a failed
 * write never leaves it on a value the server rejected. The row needs every
 * box's on-screen value to tell when a click would leave it empty.
 */
function useOptimisticTopics<Id extends TopicId>({
  topics,
  updateNotificationSetting,
}: {
  topics: Record<Id, boolean>
  updateNotificationSetting: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
}): {
  values: Record<Id, boolean>
  handleChange: (id: Id, value: boolean) => Promise<boolean>
} {
  const [pending, setPending] = useState<Partial<Record<Id, boolean>>>({})

  const handleChange = useCallback(
    async (id: Id, value: boolean): Promise<boolean> => {
      setPending((current) => ({ ...current, [id]: value }))

      const success = await updateNotificationSetting({ id, subscribed: value })

      // A later click on the same box owns it now; leave its value alone.
      setPending((current) => {
        if (current[id] !== value) return current
        const next = { ...current }
        delete next[id]
        return next
      })

      return success
    },
    [updateNotificationSetting],
  )

  return { values: { ...topics, ...pending }, handleChange }
}

/**
 * The topic checkboxes that sit under a notification switch, in one row
 * behind an `Include:` label, so a user can take it with comments only,
 * mentions only, or both.
 */
const TopicOptions = <Id extends TopicId>({
  options,
  legend,
  topics,
  onNoneChecked,
  updateNotificationSetting,
  disabled,
}: {
  options: TopicOption<Id>[]
  legend: string
  topics: Record<Id, boolean>
  /**
   * Called when a click leaves no topic checked, since a notification that is
   * on always covers something.
   */
  onNoneChecked?: () => void
  updateNotificationSetting: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
  disabled: boolean
}): JSX.Element => {
  const { values, handleChange } = useOptimisticTopics({
    topics,
    updateNotificationSetting,
  })
  const isChecked = ({ id, locked }: TopicOption<Id>): boolean =>
    locked === true || values[id]

  // A box ticked while an untick is still saving must keep the notification
  // on, so the empty-row check is repeated once the untick settles.
  const latestValues = useRef(values)
  useEffect(() => {
    latestValues.current = values
  })

  const handleTopicChange = async (id: Id, value: boolean): Promise<void> => {
    const leavesNoneChecked =
      !value &&
      options.every((option) => option.id === id || !isChecked(option))

    const success = await handleChange(id, value)

    // A failed write puts the box back, so the notification still covers it.
    if (
      success &&
      leavesNoneChecked &&
      options.every(
        (option) => option.locked !== true && !latestValues.current[option.id],
      )
    ) {
      onNoneChecked?.()
    }
  }

  return (
    <fieldset className={styles.topicOptions}>
      {/* The notification row above already names these checkboxes for
          sighted users, so the legend is for assistive technology only and
          the visible `Include:` is decorative. The legend sits outside the row
          because a hidden legend still takes a flex slot, and its gap would
          push the row out of line with the label above. */}
      <VisuallyHidden as="legend">{legend}</VisuallyHidden>
      <Flex direction="row" align="center" gap="sm">
        <Text
          size="sm"
          color="subtle"
          aria-hidden
          className={styles.topicOptionsPrefix}
        >
          Include:
        </Text>
        {options.map((option) => (
          <Checkbox
            key={option.id}
            id={`topic-${option.id}`}
            checked={isChecked(option)}
            disabled={disabled || option.locked === true}
            onChange={(value) => {
              // Popcorn's Checkbox reports an indeterminate state too, which
              // these two-state topic boxes never enter.
              void handleTopicChange(option.id, value === true)
            }}
          >
            {option.label}
          </Checkbox>
        ))}
      </Flex>
    </fieldset>
  )
}

/**
 * Drives a switch with a topic row under it. Turning the switch on stores
 * every topic in `switchOnTopicIds`. With `backfill`, a subscriber from before
 * the topics existed, who is on without any of them stored, has those topics
 * stored once.
 */
function useTopicSwitch({
  subscribed,
  switchOnTopicIds,
  showTopics,
  backfill,
  updateNotificationSetting,
  updateTopicSetting,
}: {
  subscribed: boolean
  switchOnTopicIds: readonly TopicId[]
  showTopics: boolean
  backfill?: {
    /** Whether the stored topics already give the switch something to cover. */
    hasStoredTopic: boolean
    /** False while the stored topics are loading or failed to load. */
    canBackfill: boolean
  }
  updateNotificationSetting: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
  updateTopicSetting: (options: NotificationUpdatePayload) => Promise<boolean>
}): {
  isOn: boolean
  updateSetting: (options: NotificationUpdatePayload) => Promise<boolean>
} {
  const [isOn, setIsOn] = useState(subscribed)

  useEffect(() => {
    setIsOn(subscribed)
  }, [subscribed])

  const updateSetting = useCallback(
    async (options: NotificationUpdatePayload): Promise<boolean> => {
      // The topic row follows the switch at once and goes back if a write fails.
      setIsOn(options.subscribed)

      if (!options.subscribed || !showTopics) {
        const success = await updateNotificationSetting(options)
        if (!success) setIsOn(!options.subscribed)
        return success
      }

      const [success, ...topicResults] = await Promise.all([
        updateNotificationSetting(options),
        ...switchOnTopicIds.map((id) =>
          updateTopicSetting({ id, subscribed: true }),
        ),
      ])
      const topicSuccess = topicResults.every(Boolean)

      if (success && !topicSuccess) {
        // A notification that is on always covers its topics, so without them
        // the switch goes back off. The failed write has already shown its toast.
        await updateNotificationSetting({ ...options, subscribed: false })
      }

      const isNowOn = success && topicSuccess
      if (!isNowOn) setIsOn(false)
      return isNowOn
    },
    [
      updateNotificationSetting,
      updateTopicSetting,
      showTopics,
      switchOnTopicIds,
    ],
  )

  // Decided once, on the first stored values: topics the user unticks later
  // are pending writes, not a subscriber from before the topics existed.
  const canBackfill = backfill?.canBackfill ?? false
  const hasStoredTopic = backfill?.hasStoredTopic ?? true
  const hasCheckedBackfill = useRef(false)
  useEffect(() => {
    if (
      hasCheckedBackfill.current ||
      !canBackfill ||
      !showTopics ||
      !subscribed
    ) {
      return
    }
    hasCheckedBackfill.current = true
    if (!hasStoredTopic) {
      for (const id of switchOnTopicIds) {
        void updateTopicSetting({ id, subscribed: true })
      }
    }
  }, [
    canBackfill,
    showTopics,
    subscribed,
    hasStoredTopic,
    switchOnTopicIds,
    updateTopicSetting,
  ])

  return { isOn, updateSetting }
}

const NotificationOption = ({
  preferenceData,
  updateNotificationSetting,
  pushPreferenceData,
  updatePushNotificationSetting,
  showChannelColumns = false,
  emptyPushCell = 'dash',
  disabled = false,
}: {
  preferenceData: BaseNotificationPreference
  updateNotificationSetting: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
  pushPreferenceData?: BaseNotificationPreference
  updatePushNotificationSetting?: (
    options: NotificationUpdatePayload,
  ) => Promise<boolean>
  showChannelColumns?: boolean
  /**
   * What the Push cell shows when the row has no push toggle. `dash` says
   * the topic has no push counterpart; `blank` suits a row whose one toggle
   * already covers push, such as the global unsubscribe.
   */
  emptyPushCell?: 'dash' | 'blank'
  disabled?: boolean
}): JSX.Element => {
  const { id, subscribed, name, description } = preferenceData
  const hasPushToggle =
    pushPreferenceData !== undefined &&
    updatePushNotificationSetting !== undefined

  return (
    <SettingsRow id={id}>
      <SettingsRow.Header>
        <SettingsRow.Title as="h3" size="xs">
          {name}
        </SettingsRow.Title>
        <SettingsRow.Description>{description}</SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control inline>
        {(ids) =>
          showChannelColumns ? (
            // The fixed-width Email and Push cells match the sticky column
            // labels, so the switches line up under them across every
            // section. Email comes first because every topic has one; push
            // needs the mobile app, and a topic without a push counterpart
            // shows a dash so the Push cell does not read as still loading.
            <div className={styles.toggleColumns}>
              <span className={styles.toggleCell}>
                {hasPushToggle && (
                  <VisuallyHidden id={`${id}-email-label`}>
                    Email
                  </VisuallyHidden>
                )}
                <PreferenceSwitch
                  switchId={id}
                  preferenceId={id}
                  subscribed={subscribed}
                  updateNotificationSetting={updateNotificationSetting}
                  disabled={disabled}
                  aria-labelledby={
                    hasPushToggle
                      ? `${id}-email-label ${ids.headingId}`
                      : ids.headingId
                  }
                  aria-describedby={ids.descriptionId}
                />
              </span>
              <span className={styles.toggleCell}>
                {hasPushToggle ? (
                  <>
                    <VisuallyHidden id={`${id}-push-label`}>
                      Push
                    </VisuallyHidden>
                    <PreferenceSwitch
                      switchId={`${id}-push`}
                      preferenceId={pushPreferenceData.id}
                      subscribed={pushPreferenceData.subscribed}
                      updateNotificationSetting={updatePushNotificationSetting}
                      disabled={disabled}
                      aria-labelledby={`${id}-push-label ${ids.headingId}`}
                      aria-describedby={ids.descriptionId}
                    />
                  </>
                ) : (
                  emptyPushCell === 'dash' && (
                    // Decorative: assistive technology already hears no Push
                    // switch in the row, so the dash is for sighted users.
                    <Text
                      color="subtle"
                      aria-hidden
                      data-testid={`${id}-push-unavailable`}
                    >
                      &mdash;
                    </Text>
                  )
                )}
              </span>
            </div>
          ) : (
            <PreferenceSwitch
              switchId={id}
              preferenceId={id}
              subscribed={subscribed}
              updateNotificationSetting={updateNotificationSetting}
              disabled={disabled}
              aria-labelledby={ids.headingId}
              aria-describedby={ids.descriptionId}
            />
          )
        }
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const EmailNotifications = (): JSX.Element => {
  const {
    preferencesList,
    activityAlertsBeforePreferences,
    activityAlertsAfterPreferences,
    commentNotificationsPreferences,
    insightsPerformancePreferences,
    tipsEducationPreferences,
    newslettersPreferences,
    researchFeedbackPreferences,
    updateNotificationSetting,
  } = useEmailNotificationPreferences()

  const { isEnabled: isPushNotificationsEnabled, isReady: isPushSplitReady } =
    useSplitEnabled('push-notifications-from-cio')
  const showPushToggles = isPushSplitReady && isPushNotificationsEnabled

  const currentOrganization = useCurrentOrganization()
  const trackMobileAppsLinkClick = useCallback((): void => {
    BufferTracker.cTAClicked({
      organizationId: currentOrganization?.id,
      product: 'publish',
      clientName: 'publishWeb',
      cta: 'publish-settings-notifications-tryMobileApps-1',
      upgradePathName: null,
      ...currentOrganization?.commonTrackingProperties,
    })
  }, [currentOrganization])

  const collaborationNotesSplit = useSplitEnabled('geid-collaboration-notes')

  const {
    isEnabled: isDailyRecapGranularityEnabled,
    isReady: isDailyRecapGranularityReady,
  } = useSplitEnabled('geid-cet-post-daily-granularity')
  const showDailyRecapTopics =
    isDailyRecapGranularityReady && isDailyRecapGranularityEnabled

  const {
    isEnabled: isHourlyAlertGranularityEnabled,
    isReady: isHourlyAlertGranularityReady,
  } = useSplitEnabled('geid-cet-hourly-email-granularity')
  const showHourlyAlertTopics =
    isHourlyAlertGranularityReady && isHourlyAlertGranularityEnabled

  const {
    isEnabled: isDailyCommunityRecapEnabled,
    isReady: isDailyCommunityRecapReady,
  } = useSplitEnabled('geid-cet-daily-community-recap')
  const showDailyCommunityRecap =
    isDailyCommunityRecapReady && isDailyCommunityRecapEnabled

  const {
    preferencesList: pushPreferencesList,
    updateNotificationSetting: updatePushNotificationSetting,
  } = usePushNotificationPreferences({ skip: !showPushToggles })

  const {
    dailyRecapTopics,
    hourlyAlertTopics,
    dailyCommunityRecapTopics,
    updateNotificationSetting: updateCustomerIONotificationSetting,
    isUnsubscribedFromAllEmails,
    updateGlobalUnsubscribe,
    loading: isCustomerIOLoading,
    error: customerIOError,
  } = useCustomerIOPreferences()

  // The Email/Push columns (and their header labels) appear only once push
  // preferences actually loaded: an erroring or still-loading push query keeps
  // the plain email-only layout instead of a labeled-but-empty Push column.
  const showChannelColumns = showPushToggles && pushPreferencesList !== null

  const getPushPreference = useCallback(
    (emailPreferenceId: string): BaseNotificationPreference | undefined => {
      if (!showChannelColumns) return undefined
      const pushPreferenceId = EMAIL_TO_PUSH_PREFERENCE_ID[emailPreferenceId]
      return pushPreferencesList?.find(
        (preference) => preference.id === pushPreferenceId,
      )
    },
    [showChannelColumns, pushPreferencesList],
  )

  const canBackfillTopics = !isCustomerIOLoading && !customerIOError

  // The recap always covers posts and stats, so with the topics split on that
  // box stays checked and locked, and the switch keeps the attribute stored.
  const {
    isOn: isDailyPostRecapOn,
    updateSetting: updateDailyPostRecapSetting,
  } = useTopicSwitch({
    subscribed:
      insightsPerformancePreferences?.find(
        (preference) => preference.id === DAILY_POST_RECAP_PREFERENCE_ID,
      )?.subscribed ?? false,
    switchOnTopicIds: DAILY_POST_RECAP_SWITCH_ON_TOPICS,
    showTopics: showDailyRecapTopics,
    backfill: {
      hasStoredTopic: dailyRecapTopics.postsDaily,
      canBackfill: canBackfillTopics,
    },
    updateNotificationSetting,
    updateTopicSetting: updateCustomerIONotificationSetting,
  })

  // The community recap always covers posts, so switching it on stores that
  // topic, and ticks Comments so the row is never empty while the recap is
  // on. Unticking the last topic switches it off. Subscribers from before
  // the Posts box was dropped may have neither topic stored, so both are
  // stored for them once.
  const {
    isOn: isDailyCommunityRecapOn,
    updateSetting: updateDailyCommunityRecapSetting,
  } = useTopicSwitch({
    subscribed:
      insightsPerformancePreferences?.find(
        (preference) => preference.id === DAILY_COMMUNITY_RECAP_PREFERENCE_ID,
      )?.subscribed ?? false,
    switchOnTopicIds: DAILY_COMMUNITY_RECAP_SWITCH_ON_TOPICS,
    showTopics: showDailyCommunityRecap,
    backfill: {
      hasStoredTopic:
        dailyCommunityRecapTopics.communityCommentsDaily ||
        dailyCommunityRecapTopics.communityMentionsDaily,
      canBackfill: canBackfillTopics,
    },
    updateNotificationSetting,
    updateTopicSetting: updateCustomerIONotificationSetting,
  })

  // The recaps whose switch also stores the topic it always covers.
  const recapSwitchSettings: Record<
    string,
    (options: NotificationUpdatePayload) => Promise<boolean>
  > = {
    [DAILY_POST_RECAP_PREFERENCE_ID]: updateDailyPostRecapSetting,
    [DAILY_COMMUNITY_RECAP_PREFERENCE_ID]: updateDailyCommunityRecapSetting,
  }

  // Switching the hourly alerts on ticks Comments, and unticking the last
  // topic switches them off. Subscribers from before the topics existed have
  // neither stored, so Comments is stored for them once.
  const { isOn: isHourlyEmailsOn, updateSetting: updateHourlyEmailsSetting } =
    useTopicSwitch({
      subscribed:
        commentNotificationsPreferences?.find(
          (preference) => preference.id === HOURLY_EMAILS_PREFERENCE_ID,
        )?.subscribed ?? false,
      switchOnTopicIds: HOURLY_ALERT_SWITCH_ON_TOPICS,
      showTopics: showHourlyAlertTopics,
      backfill: {
        hasStoredTopic:
          hourlyAlertTopics.commentsHourly || hourlyAlertTopics.mentionsHourly,
        canBackfill: canBackfillTopics,
      },
      updateNotificationSetting,
      updateTopicSetting: updateCustomerIONotificationSetting,
    })

  const renderEmailPreferenceWith = (
    emailNotificationPreference: BaseNotificationPreference,
    updateSetting: (options: NotificationUpdatePayload) => Promise<boolean>,
  ): JSX.Element => (
    <NotificationOption
      key={emailNotificationPreference.id}
      preferenceData={hourlyEmailsPreferenceForDisplay(
        dailyPostRecapPreferenceForDisplay(
          collaborationPreferenceForDisplay(
            emailNotificationPreference,
            collaborationNotesSplit,
          ),
          showDailyRecapTopics,
        ),
        showHourlyAlertTopics,
      )}
      updateNotificationSetting={updateSetting}
      pushPreferenceData={getPushPreference(emailNotificationPreference.id)}
      updatePushNotificationSetting={updatePushNotificationSetting}
      showChannelColumns={showChannelColumns}
      disabled={isUnsubscribedFromAllEmails}
    />
  )

  const renderEmailPreference = (
    emailNotificationPreference: BaseNotificationPreference,
  ): JSX.Element =>
    renderEmailPreferenceWith(
      emailNotificationPreference,
      updateNotificationSetting,
    )

  const globalUnsubscribePreference: BaseNotificationPreference = {
    id: 'globalUnsubscribe',
    name: 'Unsubscribe from all emails and notifications',
    description:
      'Stops all Buffer emails and push notifications, overriding the settings above. Turn it off to manage them individually.',
    subscribed: isUnsubscribedFromAllEmails,
  }

  const isLoading = !preferencesList

  if (isLoading) {
    return (
      <NewSettingsPageLayout.Root
        pageTitle="Settings - Notifications"
        testId="email-notifications-skeleton"
      >
        <Flex as="header" align="start" fullWidth>
          <NewSettingsPageLayout.Title>
            Notifications
          </NewSettingsPageLayout.Title>
        </Flex>
        <NewSettingsPageLayout.Content>
          {ACTIVITY_ALERTS_GROUP.map((option) => (
            <EmailNotificationsSkeleton key={option} />
          ))}
          {INSIGHTS_PERFORMANCE_GROUP.map((option) => (
            <EmailNotificationsSkeleton key={option} />
          ))}
          {TIPS_EDUCATION_GROUP.map((option) => (
            <EmailNotificationsSkeleton key={option} />
          ))}
          {NEWSLETTERS_GROUP.map((option) => (
            <EmailNotificationsSkeleton key={option} />
          ))}
          {RESEARCH_FEEDBACK_GROUP.map((option) => (
            <EmailNotificationsSkeleton key={option} />
          ))}
        </NewSettingsPageLayout.Content>
      </NewSettingsPageLayout.Root>
    )
  }

  return (
    <NewSettingsPageLayout.Root
      pageTitle="Settings - Notifications"
      testId="email-notifications-page"
    >
      {/* GLOBAL UNSUBSCRIBE NOTICE — above the header so it never sits under
          the sticky title row. */}
      {isUnsubscribedFromAllEmails && (
        <Notice variant="warning">
          <Notice.Text>
            You&rsquo;re unsubscribed from all Buffer emails and push
            notifications, so the settings below can&rsquo;t be changed.
          </Notice.Text>
          <Notice.Actions>
            <Button
              size="small"
              onClick={() => {
                void updateGlobalUnsubscribe(false)
              }}
            >
              Enable notifications
            </Button>
          </Notice.Actions>
        </Notice>
      )}

      <Flex as="header" align="start" fullWidth className={styles.stickyHeader}>
        <NewSettingsPageLayout.Title>Notifications</NewSettingsPageLayout.Title>
      </Flex>

      <NewSettingsPageLayout.Content gap="xl">
        {/* The zero-height wrapper starts the labels on the "Activity &
            Alerts" heading line; on scroll they ride up and dock onto the
            sticky title's line. The switches carry their own hidden channel
            labels, so the visible column labels are decoration for sighted
            users only — but the info button is real, since the mobile-app
            requirement is stated nowhere else. */}
        {showChannelColumns && (
          <div
            className={styles.columnLabelsSticky}
            data-testid="notification-channel-labels"
          >
            <div className={styles.columnLabels}>
              <Text size="sm" weight="medium" color="subtle" aria-hidden>
                Email
              </Text>
              <span className={styles.pushLabel}>
                <Text size="sm" weight="medium" color="subtle" aria-hidden>
                  Push
                </Text>
                {/* Popover, not Tooltip: the link would be unreachable by
                    keyboard inside a hover tooltip. */}
                <Popover>
                  <Popover.Trigger>
                    <button
                      type="button"
                      className={styles.pushInfoButton}
                      aria-label="About push notifications"
                    >
                      <InfoIcon size="xsmall" color="subtle" />
                    </button>
                  </Popover.Trigger>
                  <Popover.Content size="small" side="top" align="center" arrow>
                    <Text size="sm">
                      Push notifications are sent through the Buffer mobile
                      apps.{' '}
                      <Link
                        href="https://buffer.com/mobile"
                        external
                        onClick={trackMobileAppsLinkClick}
                      >
                        Try them out
                      </Link>
                    </Text>
                  </Popover.Content>
                </Popover>
              </span>
            </div>
          </div>
        )}

        {/* ACTIVITY & ALERTS */}
        <Flex direction="column" gap="lg" fullWidth>
          <Heading as="h2" size="small">
            Activity & Alerts
          </Heading>

          {/* First part: Post Failures, Channel Connection Updates */}
          {activityAlertsBeforePreferences?.map(renderEmailPreference)}

          {/* Hourly Comment Alerts */}
          {commentNotificationsPreferences?.map((preference) => (
            <Fragment key={preference.id}>
              {preference.id === HOURLY_EMAILS_PREFERENCE_ID
                ? renderEmailPreferenceWith(
                    preference,
                    updateHourlyEmailsSetting,
                  )
                : renderEmailPreference(preference)}
              {showHourlyAlertTopics &&
                isHourlyEmailsOn &&
                preference.id === HOURLY_EMAILS_PREFERENCE_ID && (
                  <TopicOptions
                    options={HOURLY_ALERT_TOPICS}
                    legend={`${HOURLY_EMAILS_TOPICS_NAME} contents`}
                    topics={hourlyAlertTopics}
                    onNoneChecked={() => {
                      void updateHourlyEmailsSetting({
                        id: HOURLY_EMAILS_PREFERENCE_ID,
                        subscribed: false,
                      })
                    }}
                    updateNotificationSetting={
                      updateCustomerIONotificationSetting
                    }
                    disabled={isUnsubscribedFromAllEmails}
                  />
                )}
            </Fragment>
          ))}

          {/* Second part: Collaboration, Published Post Confirmations, Empty Queue Alerts, Billing */}
          {activityAlertsAfterPreferences?.map(renderEmailPreference)}
        </Flex>

        {/* INSIGHTS & PERFORMANCE */}
        <Separator className={styles.separator} />

        <Flex direction="column" gap="lg" fullWidth>
          <Heading as="h2" size="small">
            Insights & Performance
          </Heading>

          {insightsPerformancePreferences
            ?.filter(
              (preference) =>
                showDailyCommunityRecap ||
                preference.id !== DAILY_COMMUNITY_RECAP_PREFERENCE_ID,
            )
            .map((preference) => (
              <Fragment key={preference.id}>
                {renderEmailPreferenceWith(
                  preference,
                  recapSwitchSettings[preference.id] ??
                    updateNotificationSetting,
                )}
                {showDailyRecapTopics &&
                  isDailyPostRecapOn &&
                  preference.id === DAILY_POST_RECAP_PREFERENCE_ID && (
                    <TopicOptions
                      options={DAILY_RECAP_TOPICS}
                      legend="Daily Post Recap contents"
                      topics={dailyRecapTopics}
                      updateNotificationSetting={
                        updateCustomerIONotificationSetting
                      }
                      disabled={isUnsubscribedFromAllEmails}
                    />
                  )}
                {isDailyCommunityRecapOn &&
                  preference.id === DAILY_COMMUNITY_RECAP_PREFERENCE_ID && (
                    <TopicOptions
                      options={DAILY_COMMUNITY_RECAP_TOPICS}
                      legend="Daily Community Recap contents"
                      topics={dailyCommunityRecapTopics}
                      onNoneChecked={() => {
                        void updateDailyCommunityRecapSetting({
                          id: DAILY_COMMUNITY_RECAP_PREFERENCE_ID,
                          subscribed: false,
                        })
                      }}
                      updateNotificationSetting={
                        updateCustomerIONotificationSetting
                      }
                      disabled={isUnsubscribedFromAllEmails}
                    />
                  )}
              </Fragment>
            ))}
        </Flex>

        {/* TIPS & PRODUCT EDUCATION */}
        <Separator className={styles.separator} />

        <Flex direction="column" gap="lg" fullWidth>
          <Heading as="h2" size="small">
            Tips & Product Education
          </Heading>

          {tipsEducationPreferences?.map(renderEmailPreference)}
        </Flex>

        {/* NEWSLETTERS */}
        <Separator className={styles.separator} />

        <Flex direction="column" gap="lg" fullWidth>
          <Heading as="h2" size="small">
            Newsletters
          </Heading>

          {newslettersPreferences?.map(renderEmailPreference)}
        </Flex>

        {/* RESEARCH & FEEDBACK */}
        <Separator className={styles.separator} />

        <Flex direction="column" gap="lg" fullWidth>
          <Heading as="h2" size="small">
            Research & Feedback
          </Heading>

          {researchFeedbackPreferences?.map(renderEmailPreference)}
        </Flex>

        {/* UNSUBSCRIBE */}
        <Separator className={styles.separator} />

        <Flex direction="column" gap="lg" fullWidth>
          <Heading as="h2" size="small">
            Unsubscribe
          </Heading>

          {/* Shares the column grid so the toggle lines up under Email
              rather than sitting flush right, a few pixels out from the
              rows above. The Push cell stays blank: this one toggle already
              covers push, so a dash would read as "push not included". */}
          <NotificationOption
            preferenceData={globalUnsubscribePreference}
            updateNotificationSetting={({ subscribed }) =>
              updateGlobalUnsubscribe(subscribed)
            }
            showChannelColumns={showChannelColumns}
            emptyPushCell="blank"
          />
        </Flex>
      </NewSettingsPageLayout.Content>
    </NewSettingsPageLayout.Root>
  )
}

export default EmailNotifications
